import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Inventory as ProductIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { ProductSearchDialog } from './ProductSearchDialog';
import { Article } from '../../../../core/interfaces/article.interface';
import { ProductAttribute } from '../../../../core/interfaces/productAttribute.interface';
import { useArticlesCore } from '../../../../core/modules/articles';
import { useMasterCore } from '../../../../core/modules/master';
import { usePrintLabel } from '../../../../core/hooks/usePrintLabel';
import CreateArticle from '../../../screens/dashboard/administration/articles/CreateArticle';

interface ProductSelectionProps {
  onProductSelect: (product: any | null) => void;
  companyId?: number;
  resetKey?: number;
}

type AttributeValueKey =
  | 'usage_id'
  | 'packaging_id'
  | 'functionality_id'
  | 'aesthetic_id'
  | 'completeness_id'
  | 'state_id';

type AttributeNoteKey =
  | 'usage_note'
  | 'packaging_note'
  | 'functionality_note'
  | 'aesthetic_note'
  | 'completeness_note'
  | 'observation';

interface ProductClassificationData {
  product_id?: number;
  width_cm?: number;
  length_cm?: number;
  depth_cm?: number;
  weight_kg?: number;
  volumetric_weight_kg?: number;
  quantity?: number;
  reference: string;
  observation: string;
  usage_id?: number;
  packaging_id?: number;
  functionality_id?: number;
  aesthetic_id?: number;
  completeness_id?: number;
  state_id?: number;
  usage_note: string;
  packaging_note: string;
  functionality_note: string;
  aesthetic_note: string;
  completeness_note: string;
}

interface AttributeDefinition {
  key: string;
  label: string;
  valueKey: AttributeValueKey;
  noteKey: AttributeNoteKey;
  options: ProductAttribute[];
  placeholder: string;
}

const INITIAL_PRODUCT_DATA: ProductClassificationData = {
  product_id: undefined,
  width_cm: undefined,
  length_cm: undefined,
  depth_cm: undefined,
  weight_kg: undefined,
  volumetric_weight_kg: undefined,
  quantity: undefined,
    reference: '',
    observation: '',
  usage_id: undefined,
  packaging_id: undefined,
  functionality_id: undefined,
  aesthetic_id: undefined,
  completeness_id: undefined,
  state_id: undefined,
    usage_note: '',
    packaging_note: '',
    functionality_note: '',
    aesthetic_note: '',
    completeness_note: '',
};

export const ProductSelection: React.FC<ProductSelectionProps> = ({
  onProductSelect,
  companyId,
  resetKey = 0,
}) => {
  const { getArticle } = useArticlesCore();
  const {
    productUsages,
    productPackagings,
    productFuntionalities,
    productAesthetics,
    productCompletenesses,
    productStates,
    getProductAttributes,
  } = useMasterCore();
  const { printArticle } = usePrintLabel();
  const [showProductForm, setShowProductForm] = useState(false);
  const [localSelectedProduct, setLocalSelectedProduct] = useState<Article | null>(null);
  const [productSearchInput, setProductSearchInput] = useState('');
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isEditArticleOpen, setIsEditArticleOpen] = useState(false);

  const onProductSelectRef = useRef(onProductSelect);

  useEffect(() => {
    onProductSelectRef.current = onProductSelect;
  }, [onProductSelect]);

  // Estados para los campos del producto
  const [productData, setProductData] = useState<ProductClassificationData>({
    ...INITIAL_PRODUCT_DATA,
  });

  const handleInputChange = <K extends keyof ProductClassificationData>(
    field: K,
    value: ProductClassificationData[K]
  ) => {
    setProductData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (!companyId) {
      return;
    }

    if (!productUsages || productUsages.length === 0) {
      getProductAttributes(companyId, 'usage');
    }
    if (!productPackagings || productPackagings.length === 0) {
      getProductAttributes(companyId, 'packaging');
    }
    if (!productFuntionalities || productFuntionalities.length === 0) {
      getProductAttributes(companyId, 'functionality');
    }
    if (!productAesthetics || productAesthetics.length === 0) {
      getProductAttributes(companyId, 'aesthetic');
    }
    if (!productCompletenesses || productCompletenesses.length === 0) {
      getProductAttributes(companyId, 'completeness');
    }
    if (!productStates || productStates.length === 0) {
      getProductAttributes(companyId, 'state');
    }
  }, [
    companyId,
    productUsages,
    productPackagings,
    productFuntionalities,
    productAesthetics,
    productCompletenesses,
    productStates,
    getProductAttributes,
  ]);

  // Mantener el orden del backend (ordenado por cardinality)
  // No ordenar alfabéticamente para respetar el orden configurado en el maestro
  const sortedProductUsages = useMemo(
    () => productUsages ?? [],
    [productUsages]
  );

  const sortedProductPackagings = useMemo(
    () => productPackagings ?? [],
    [productPackagings]
  );

  const sortedProductFunctionalities = useMemo(
    () => productFuntionalities ?? [],
    [productFuntionalities]
  );

  const sortedProductAesthetics = useMemo(
    () => productAesthetics ?? [],
    [productAesthetics]
  );

  const sortedProductCompletenesses = useMemo(
    () => productCompletenesses ?? [],
    [productCompletenesses]
  );

  const sortedProductStates = useMemo(
    () => productStates ?? [],
    [productStates]
  );

  const attributeDefinitions: AttributeDefinition[] = useMemo(
    () => [
      {
        key: 'usage',
        label: 'Uso',
        valueKey: 'usage_id',
        noteKey: 'usage_note',
        options: sortedProductUsages,
        placeholder: 'Observación sobre el uso del producto',
      },
      {
        key: 'packaging',
        label: 'Empaquetado',
        valueKey: 'packaging_id',
        noteKey: 'packaging_note',
        options: sortedProductPackagings,
        placeholder: 'Observación sobre el empaquetado',
      },
      {
        key: 'functionality',
        label: 'Funcionalidad',
        valueKey: 'functionality_id',
        noteKey: 'functionality_note',
        options: sortedProductFunctionalities,
        placeholder: 'Observación sobre la funcionalidad',
      },
      {
        key: 'aesthetic',
        label: 'Estético',
        valueKey: 'aesthetic_id',
        noteKey: 'aesthetic_note',
        options: sortedProductAesthetics,
        placeholder: 'Observación sobre el estado estético',
      },
      {
        key: 'completeness',
        label: 'Completitud',
        valueKey: 'completeness_id',
        noteKey: 'completeness_note',
        options: sortedProductCompletenesses,
        placeholder: 'Observación sobre la completitud',
      },
      {
        key: 'state',
        label: 'Estado',
        valueKey: 'state_id',
        noteKey: 'observation',
        options: sortedProductStates,
        placeholder: 'Observación sobre el estado',
      },
    ],
    [
      sortedProductUsages,
      sortedProductPackagings,
      sortedProductFunctionalities,
      sortedProductAesthetics,
      sortedProductCompletenesses,
      sortedProductStates,
    ]
  );

  const handleAttributeChange = (definition: AttributeDefinition, rawValue: string) => {
    const selectedId = rawValue === '' ? undefined : Number(rawValue);
    const selectedAttribute = definition.options.find((attribute) => attribute.id === selectedId);

    setProductData((prev) => ({
      ...prev,
      [definition.valueKey]: selectedId,
      [definition.noteKey]: selectedAttribute?.has_description ? prev[definition.noteKey] ?? '' : '',
    }));
  };

  const handleNoteFieldChange = (noteKey: AttributeNoteKey, value: string) => {
    setProductData((prev) => ({
      ...prev,
      [noteKey]: value,
    }));
  };

  // Resetear clasificación cuando cambie resetKey (mantener producto)
  useEffect(() => {
    if (resetKey > 0 && localSelectedProduct) {
      // Solo resetear los datos de clasificación, mantener el producto
      setProductData((prev) => {
        const newData = {
          ...INITIAL_PRODUCT_DATA,
          product_id: prev.product_id, // Mantener el producto
          width_cm: localSelectedProduct?.width_cm,
          length_cm: localSelectedProduct?.length_cm,
          depth_cm: localSelectedProduct?.depth_cm,
          weight_kg: localSelectedProduct?.weight_kg,
          volumetric_weight_kg: localSelectedProduct?.volumetric_weight_kg,
          quantity: 0,
        };
        // Establecer primeros estados por defecto
        if (sortedProductUsages.length > 0) {
          newData.usage_id = sortedProductUsages[0].id;
        }
        if (sortedProductPackagings.length > 0) {
          newData.packaging_id = sortedProductPackagings[0].id;
        }
        if (sortedProductFunctionalities.length > 0) {
          newData.functionality_id = sortedProductFunctionalities[0].id;
        }
        if (sortedProductAesthetics.length > 0) {
          newData.aesthetic_id = sortedProductAesthetics[0].id;
        }
        if (sortedProductCompletenesses.length > 0) {
          newData.completeness_id = sortedProductCompletenesses[0].id;
        }
        if (sortedProductStates.length > 0) {
          newData.state_id = sortedProductStates[0].id;
        }
        return newData;
      });
    }
  }, [resetKey, localSelectedProduct, sortedProductUsages, sortedProductPackagings, sortedProductFunctionalities, sortedProductAesthetics, sortedProductCompletenesses, sortedProductStates]);

  // Actualizar automáticamente cuando cambien los datos del producto
  useEffect(() => {
    if (localSelectedProduct) {
      // Inicializar los datos del producto con los valores del producto seleccionado
      setProductData(prev => ({
        ...prev,
        product_id: localSelectedProduct.id,
        width_cm: localSelectedProduct.width_cm ?? prev.width_cm,
        length_cm: localSelectedProduct.length_cm ?? prev.length_cm,
        depth_cm: localSelectedProduct.depth_cm ?? prev.depth_cm,
        weight_kg: localSelectedProduct.weight_kg ?? prev.weight_kg,
        volumetric_weight_kg: localSelectedProduct.volumetric_weight_kg ?? prev.volumetric_weight_kg,
        quantity: prev.quantity !== undefined ? prev.quantity : 0,
        // Establecer primeros estados por defecto si no están definidos
        usage_id: prev.usage_id || (sortedProductUsages.length > 0 ? sortedProductUsages[0].id : undefined),
        packaging_id: prev.packaging_id || (sortedProductPackagings.length > 0 ? sortedProductPackagings[0].id : undefined),
        functionality_id: prev.functionality_id || (sortedProductFunctionalities.length > 0 ? sortedProductFunctionalities[0].id : undefined),
        aesthetic_id: prev.aesthetic_id || (sortedProductAesthetics.length > 0 ? sortedProductAesthetics[0].id : undefined),
        completeness_id: prev.completeness_id || (sortedProductCompletenesses.length > 0 ? sortedProductCompletenesses[0].id : undefined),
        state_id: prev.state_id || (sortedProductStates.length > 0 ? sortedProductStates[0].id : undefined),
      }));
    }
  }, [localSelectedProduct, sortedProductStates, sortedProductUsages, sortedProductPackagings, sortedProductFunctionalities, sortedProductAesthetics, sortedProductCompletenesses]);

  useEffect(() => {
    if (!localSelectedProduct) {
      return;
    }

    onProductSelectRef.current({
      ...productData,
      product_id: productData.product_id ?? localSelectedProduct.id,
    });
  }, [localSelectedProduct, productData]);

  // Función para buscar artículo por código
  const handleProductSearch = useCallback(() => {
    if (!companyId) return;
    const value = productSearchInput.trim();
    if (!value) return;
    getArticle(companyId, { code: value }, (article) => {
      if (article) {
        setLocalSelectedProduct(article);
      }
    });
  }, [productSearchInput, companyId, getArticle]);

  const imageUrls = useMemo(() => {
    if (!localSelectedProduct?.files) return [] as string[];
    return localSelectedProduct.files
      .map((file) => file.URL)
      .filter((url): url is string => Boolean(url));
  }, [localSelectedProduct]);

  const hasImages = imageUrls.length > 0;

  useEffect(() => {
    setSelectedImageIndex(0);
    setIsImageDialogOpen(false);
  }, [imageUrls.length]);

  const handleOpenImageDialog = (index = 0) => {
    if (!hasImages) return;
    setSelectedImageIndex(index);
    setIsImageDialogOpen(true);
  };

  const handleCloseImageDialog = () => {
    setIsImageDialogOpen(false);
  };

  const handlePrevImage = () => {
    if (!hasImages) return;
    setSelectedImageIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length);
  };

  const handleNextImage = () => {
    if (!hasImages) return;
    setSelectedImageIndex((prev) => (prev + 1) % imageUrls.length);
  };

  if (!localSelectedProduct) {
    return (
      <>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Selección de Producto
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Debes seleccionar un producto para continuar con la clasificación.
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  placeholder="Escanear o buscar por código"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={productSearchInput}
                  onChange={(e) => setProductSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleProductSearch();
                    }
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleProductSearch}
                  disabled={!productSearchInput.trim()}
                >
                  Buscar
                </Button>
                <IconButton
                  onClick={() => setShowProductForm(true)}
                  size="small"
                  sx={{ 
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    backgroundColor: 'white',
                    '&:hover': { backgroundColor: '#f5f5f5' }
                  }}
                >
                  <FilterListIcon />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <ProductSearchDialog
          open={showProductForm}
          onClose={() => setShowProductForm(false)}
          onSelect={(product) => {
            setLocalSelectedProduct(product);
            setProductSearchInput(product.code || '');
            setShowProductForm(false);
          }}
          title="Buscar Producto"
          companyId={companyId}
        />
      </>
    );
  }

  return (
    <Box>
      {/* Información del producto seleccionado */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: hasImages ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: hasImages ? 'transparent' : 'grey.100',
                }}
                onClick={hasImages ? () => handleOpenImageDialog(0) : undefined}
              >
                {hasImages ? (
                  <Box
                    component="img"
                    src={imageUrls[0]}
                    alt={localSelectedProduct.name}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
              <ProductIcon color="primary" sx={{ fontSize: 40 }} />
                )}
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  fontWeight="bold"
                  sx={{ 
                    color: 'primary.main',
                    fontSize: '1.2rem'
                  }}
                >
                  {localSelectedProduct.code || 'Sin código'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {localSelectedProduct.description || 'Sin descripción'}
                </Typography>
              </Box>
            </Box>
            
            <Box display="flex" gap={2} alignItems="center">
             <Button
               variant="outlined"
               color="secondary"
               size="small"
               onClick={() => {
                 setLocalSelectedProduct(null);
                 setProductData({ ...INITIAL_PRODUCT_DATA });
                 setIsImageDialogOpen(false);
                 setSelectedImageIndex(0);
                 setProductSearchInput('');
                 onProductSelectRef.current(null);
               }}
             >
                Limpiar
             </Button>
             <Button
               variant="outlined"
                startIcon={<SearchIcon />}
                size="small"
               onClick={() => {
                 setLocalSelectedProduct(null);
                  setProductData({ ...INITIAL_PRODUCT_DATA });
                  setIsImageDialogOpen(false);
                  setSelectedImageIndex(0);
                 setShowProductForm(true);
                  onProductSelectRef.current(null);
               }}
             >
                Cambiar producto
             </Button>
            {localSelectedProduct && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                size="small"
                onClick={() => setIsEditArticleOpen(true)}
              >
                Editar producto
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              size="small"
              onClick={() => localSelectedProduct && printArticle(localSelectedProduct)}
              disabled={!localSelectedProduct}
            >
              Imprimir
            </Button>
              {hasImages && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => handleOpenImageDialog(selectedImageIndex)}
                >
                  Ver fotos ({imageUrls.length})
                </Button>
              )}
            </Box>
          </Box>
          
          {localSelectedProduct && (
            <Box mt={2} display="flex" justifyContent="flex-start">
              <TextField
                label="Cantidad"
                type="number"
                size="small"
                value={productData.quantity ?? 0}
                onFocus={(e) => {
                  e.target.select();
                }}
                onChange={(e) => {
                  const { value } = e.target;
                  if (value === '') {
                    handleInputChange('quantity', 0);
                  } else {
                    const numValue = parseInt(value, 10);
                    // No permitir negativos
                    if (!isNaN(numValue) && numValue >= 0) {
                      handleInputChange('quantity', numValue);
                    }
                  }
                }}
                inputProps={{ min: 0 }}
                sx={{
                  width: '150px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    '& fieldset': {
                      borderWidth: '2px',
                      borderColor: 'primary.main',
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.dark',
                      borderWidth: '2px',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: '600',
                    color: 'primary.main',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'primary.main',
                    fontWeight: '600',
                  },
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Formulario de clasificación */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Clasificación del Producto
          </Typography>
          
          <Grid container spacing={3}>
            {/* Dimensiones y peso */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Dimensiones y Peso
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Ancho (cm)"
                type="number"
                value={productData.width_cm ?? ''}
                onChange={(e) => {
                  const { value } = e.target;
                  handleInputChange('width_cm', value === '' ? undefined : parseFloat(value));
                }}
                inputProps={{ step: 0.1, min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Largo (cm)"
                type="number"
                value={productData.length_cm ?? ''}
                onChange={(e) => {
                  const { value } = e.target;
                  handleInputChange('length_cm', value === '' ? undefined : parseFloat(value));
                }}
                inputProps={{ step: 0.1, min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Profundidad (cm)"
                type="number"
                value={productData.depth_cm ?? ''}
                onChange={(e) => {
                  const { value } = e.target;
                  handleInputChange('depth_cm', value === '' ? undefined : parseFloat(value));
                }}
                inputProps={{ step: 0.1, min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Peso (kg)"
                type="number"
                value={productData.weight_kg ?? ''}
                onChange={(e) => {
                  const { value } = e.target;
                  handleInputChange('weight_kg', value === '' ? undefined : parseFloat(value));
                }}
                inputProps={{ step: 0.001, min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Peso Volumétrico (kg)"
                type="number"
                value={productData.volumetric_weight_kg ?? ''}
                onChange={(e) => {
                  const { value } = e.target;
                  handleInputChange('volumetric_weight_kg', value === '' ? undefined : parseFloat(value));
                }}
                inputProps={{ step: 0.0001, min: 0 }}
              />
            </Grid>
            

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Estados con observaciones */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Estados del Producto
              </Typography>
            </Grid>

            {attributeDefinitions.map((definition) => {
              const selectedId = productData[definition.valueKey];
              const selectedAttribute = definition.options.find((option) => option.id === selectedId);
              const requiresObservation = Boolean(selectedAttribute?.has_description);

              return (
                <React.Fragment key={definition.key}>
                  <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                      <InputLabel>{`${definition.label} *`}</InputLabel>
                <Select
                        value={selectedId ?? ''}
                        onChange={(e) => handleAttributeChange(definition, e.target.value as string)}
                        label={`${definition.label} *`}
                      >
                        <MenuItem value="">{`Seleccionar ${definition.label.toLowerCase()}`}</MenuItem>
                        {definition.options.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
                  <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                      label={`Observación ${definition.label}`}
                      value={productData[definition.noteKey] ?? ''}
                      onChange={(e) => handleNoteFieldChange(definition.noteKey, e.target.value)}
                      placeholder={definition.placeholder}
                      disabled={!requiresObservation || !selectedId}
                      required={requiresObservation}
                      multiline
                      sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          backgroundColor: requiresObservation && selectedId ? 'transparent' : 'rgba(0, 0, 0, 0.06)',
                          WebkitTextFillColor: requiresObservation && selectedId ? 'inherit' : 'rgba(0, 0, 0, 0.38)',
                        }
                      }}
                />
              </Grid>
                </React.Fragment>
              );
            })}
          </Grid>

        </CardContent>
      </Card>
      {hasImages && (
        <Dialog
          open={isImageDialogOpen}
          onClose={handleCloseImageDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">{localSelectedProduct?.name}</Typography>
            <IconButton onClick={handleCloseImageDialog} aria-label="Cerrar">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box
              position="relative"
              display="flex"
              alignItems="center"
              justifyContent="center"
              minHeight="300px"
            >
              {imageUrls.length > 1 && (
                <IconButton
                  onClick={handlePrevImage}
                  aria-label="Imagen anterior"
                  sx={{ position: 'absolute', left: 16 }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              )}
              <Box
                component="img"
                src={imageUrls[selectedImageIndex]}
                alt={`${localSelectedProduct?.name} - Imagen ${selectedImageIndex + 1}`}
                sx={{
                  width: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 2,
                  boxShadow: 3,
                }}
              />
              {imageUrls.length > 1 && (
                <IconButton
                  onClick={handleNextImage}
                  aria-label="Imagen siguiente"
                  sx={{ position: 'absolute', right: 16 }}
                >
                  <ChevronRightIcon />
                </IconButton>
              )}
            </Box>
            {imageUrls.length > 1 && (
              <Box
                mt={2}
                display="flex"
                justifyContent="center"
                gap={1}
                flexWrap="wrap"
              >
                {imageUrls.map((url, idx) => (
                  <Box
                    key={`${url}-${idx}`}
                    component="img"
                    src={url}
                    alt={`${localSelectedProduct?.name} miniatura ${idx + 1}`}
                    onClick={() => setSelectedImageIndex(idx)}
                    sx={{
                      width: 64,
                      height: 64,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor:
                        idx === selectedImageIndex ? 'primary.main' : 'transparent',
                      boxShadow: idx === selectedImageIndex ? 3 : 1,
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseImageDialog}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      )}
      {localSelectedProduct && (
        <Dialog open={isEditArticleOpen} onClose={() => setIsEditArticleOpen(false)} fullScreen>
          <CreateArticle
            embedded
            mode="edit"
            initialArticle={localSelectedProduct}
            onClose={() => setIsEditArticleOpen(false)}
            onUpdated={(article) => {
              setLocalSelectedProduct(article);
              setProductSearchInput(article.code || '');
              setProductData((prev) => ({
                ...prev,
                product_id: article.id,
                width_cm: article.width_cm ?? prev.width_cm,
                length_cm: article.length_cm ?? prev.length_cm,
                depth_cm: article.depth_cm ?? prev.depth_cm,
                weight_kg: article.weight_kg ?? prev.weight_kg,
                volumetric_weight_kg: article.volumetric_weight_kg ?? prev.volumetric_weight_kg,
              }));
              setIsEditArticleOpen(false);
            }}
          />
        </Dialog>
      )}
    </Box>
  );
};
